import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { notices, faqs } from '../../mocks/programs';
import { useState, useEffect } from 'react';

const SupportPage = () => {
  const [activeTab, setActiveTab] = useState<'notice' | 'faq'>('notice');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '') as 'notice' | 'faq';
    if (hash && ['notice', 'faq'].includes(hash)) {
      setActiveTab(hash);
    }
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-support';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "name": "Branch(브란치) 고객지원 | 공지사항 및 자주 묻는 질문",
        "description": "Branch(브란치) 프로그램에 관한 공지사항과 자주 묻는 질문(FAQ)을 확인하세요. 국제교류, 원데이 클래스, 대학 탐방, 진로 특강 관련 안내.",
        "url": siteUrl + "/support",
        "inLanguage": "ko-KR",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Branch(브란치)",
          "url": siteUrl
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "홈", "item": siteUrl + "/" },
            { "@type": "ListItem", "position": 2, "name": "고객지원", "item": siteUrl + "/support" }
          ]
        },
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 px-4 md:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C2416] mb-6 md:mb-8 tracking-tight" style={{ fontFamily: 'Pretendard, sans-serif' }}>
            고객지원
          </h1>
          <p className="text-lg md:text-xl text-[#8B7355] leading-relaxed max-w-2xl mx-auto">
            Branch와 함께하는 여정에서 필요한 모든 정보를 제공합니다
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-sm border-b border-[#F5EFE7]">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex gap-8 md:gap-12 py-4 md:py-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('notice')}
              className={`text-base md:text-lg font-medium transition-all whitespace-nowrap cursor-pointer pb-2 border-b-2 flex-shrink-0 ${
                activeTab === 'notice'
                  ? 'border-[#8B7355] text-[#2C2416]'
                  : 'border-transparent text-[#8B7355] hover:text-[#2C2416]'
              }`}
              style={{ fontFamily: 'Pretendard, sans-serif' }}
            >
              공지사항
            </button>
            <button
              onClick={() => setActiveTab('faq')}
              className={`text-base md:text-lg font-medium transition-all whitespace-nowrap cursor-pointer pb-2 border-b-2 flex-shrink-0 ${
                activeTab === 'faq'
                  ? 'border-[#8B7355] text-[#2C2416]'
                  : 'border-transparent text-[#8B7355] hover:text-[#2C2416]'
              }`}
              style={{ fontFamily: 'Pretendard, sans-serif' }}
            >
              자주 묻는 질문
            </button>
          </div>
        </div>
      </section>

      {/* Notice Section */}
      {activeTab === 'notice' && (
        <section id="notice" className="py-16 md:py-24 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="space-y-1">
              {notices.map((notice, index) => (
                <div
                  key={notice.id}
                  className="group border-b border-[#F5EFE7] py-6 md:py-8 hover:bg-[#FDFBF7] transition-all cursor-pointer px-4 md:px-8"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-8">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 flex-1">
                      <span className="text-[#8B7355]/40 text-sm md:text-base font-light md:w-12 md:text-center">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 md:gap-4 mb-2 md:mb-3">
                          <span className={`text-xs md:text-sm font-medium tracking-wider ${
                            notice.category === '모집공고' 
                              ? 'text-[#8B7355]'
                              : 'text-[#2C2416]'
                          }`}>
                            {notice.category}
                          </span>
                          {notice.isNew && (
                            <span className="w-1.5 h-1.5 bg-[#8B7355] rounded-full"></span>
                          )}
                        </div>
                        <h3 className="text-lg md:text-xl font-medium text-[#2C2416] mb-2 group-hover:text-[#8B7355] transition-colors" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                          {notice.title}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 md:gap-8 justify-between md:justify-start">
                      <span className="text-sm md:text-base text-[#8B7355]/60 font-light">{notice.date}</span>
                      <i className="ri-arrow-right-line text-lg md:text-xl text-[#8B7355]/40 group-hover:text-[#8B7355] group-hover:translate-x-1 transition-all w-5 h-5 md:w-6 md:h-6 flex items-center justify-center"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {activeTab === 'faq' && (
        <section id="faq" className="py-16 md:py-24 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="space-y-1">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className="border-b border-[#F5EFE7] overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                    className="w-full py-6 md:py-8 px-4 md:px-8 flex items-center justify-between gap-4 md:gap-8 text-left cursor-pointer hover:bg-[#FDFBF7] transition-all group"
                  >
                    <div className="flex items-start gap-4 md:gap-8 flex-1">
                      <span className="text-[#8B7355]/40 text-sm md:text-base font-light md:w-12 md:text-center flex-shrink-0 mt-1">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-base md:text-xl font-medium text-[#2C2416] leading-relaxed group-hover:text-[#8B7355] transition-colors" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                        {faq.question}
                      </h3>
                    </div>
                    <i className={`text-lg md:text-xl text-[#8B7355]/40 transition-all flex-shrink-0 w-5 h-5 md:w-6 md:h-6 flex items-center justify-center ${
                      expandedFaq === faq.id ? 'ri-subtract-line rotate-0' : 'ri-add-line'
                    }`}></i>
                  </button>
                  
                  {expandedFaq === faq.id && (
                    <div className="px-4 md:px-8 pb-6 md:pb-8 bg-[#FDFBF7]">
                      <div className="flex items-start gap-4 md:gap-8 md:pl-20">
                        <p className="text-[#8B7355] text-sm md:text-lg leading-relaxed flex-1 font-light">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div className="mt-20 md:mt-32 bg-transparent border-2 border-white rounded-2xl p-8 md:p-16 text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                더 궁금하신 사항이 있으신가요?
              </h3>
              <p className="text-[#8B7355] text-base md:text-lg mb-8 md:mb-12 leading-relaxed font-light">
                프로그램 신청 페이지를 통해 문의사항을 남겨주시면<br className="hidden md:block" />
                빠른 시일 내에 답변드리겠습니다.
              </p>
              <a
                href="/apply"
                className="inline-flex items-center gap-3 bg-[#8B7355] text-white px-8 md:px-10 py-3 md:py-4 rounded-full font-medium hover:bg-[#2C2416] transition-all whitespace-nowrap cursor-pointer"
              >
                <span>문의하기</span>
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default SupportPage;
